// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a Teams meeting inside Konvo (desktop app). Microsoft's own Teams web app runs in a
// <webview> in Konvo's main area, signed in as the person, so it is free: their Microsoft 365
// licence covers it. Konvo draws the header and the controls around it; its buttons press Teams'
// own buttons (the desktop app's Teams preload) and Teams' state comes back within a few
// milliseconds. Going elsewhere in Konvo keeps the meeting in a small window bottom left. When
// Teams' page changes and its buttons can't be found, Konvo's controls step back and Teams' own
// controls stay visible. Older desktop apps (no teamsPrepareMeeting) keep the separate Teams window.

import TeamsFollow from '@app/features/konvo_teams_meetings/state/TeamsFollow';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('TeamsInKonvo');

export type TeamsControl =
	| 'mic'
	| 'camera'
	| 'share'
	| 'hand'
	| 'captions'
	| 'captions-on'
	| 'people'
	| 'chat'
	| 'leave'
	| 'show-teams-controls'
	| 'hide-teams-controls';

export interface TeamsPageState {
	phase: 'launcher' | 'prejoin' | 'lobby' | 'meeting' | 'other';
	muted: boolean | null;
	camera: boolean | null;
	sharing: boolean | null;
	hand: boolean | null;
	captions: boolean;
	recording: boolean;
	controls: boolean;
}

export interface InKonvoMeeting {
	url: string;
	title: string | null;
	partition: string;
	src: string;
}

const LOADING: TeamsPageState = {
	phase: 'other',
	muted: null,
	camera: null,
	sharing: null,
	hand: null,
	captions: false,
	recording: false,
	controls: false,
};

/** Teams' page left the meeting this long ago (its after-call page): Konvo closes it. */
const CLOSE_AFTER_LEFT_MS = 4000;
/** Leave pressed: Teams gets this long to hang up properly before the page goes. */
const CLOSE_AFTER_LEAVE_MS = 1500;
/** Joined and Konvo follows the meeting: live captions are turned on after this long. */
const CAPTIONS_AFTER_MS = 3000;

class TeamsInKonvoStore {
	meeting: InKonvoMeeting | null = null;
	state: TeamsPageState = LOADING;
	minimised = false;
	/** Teams' own toolbar shown too (Konvo's More), for what Konvo's controls don't cover. */
	teamsControlsShown = false;
	/** When the person got into the meeting (Teams' page showed its meeting controls). */
	joinedAt: number | null = null;

	private started = false;
	private leftTimer: ReturnType<typeof setTimeout> | null = null;
	private captionsAsked = false;

	constructor() {
		makeAutoObservable<TeamsInKonvoStore, 'started' | 'leftTimer' | 'captionsAsked'>(
			this,
			{started: false, leftTimer: false, captionsAsked: false},
			{autoBind: true},
		);
	}

	get active(): boolean {
		return this.meeting !== null;
	}

	get inMeeting(): boolean {
		return this.state.phase === 'meeting';
	}

	/** Konvo's own controls stand in for Teams' (Teams' buttons were found). */
	get konvoControls(): boolean {
		return this.inMeeting && this.state.controls;
	}

	/** Whether this desktop app can show Teams meetings inside Konvo. */
	get supported(): boolean {
		return Boolean(getElectronAPI()?.teamsPrepareMeeting);
	}

	/** Listens to the desktop app's Teams page (once). */
	start(): void {
		if (this.started) return;
		const desktop = getElectronAPI();
		if (!desktop?.onTeamsMeeting) return;
		this.started = true;
		desktop.onTeamsMeeting((event) => {
			if (event.type === 'state') this.onState(event.state);
			else if (event.type === 'end' && this.meeting) this.close();
		});
	}

	/** Opens a meeting inside Konvo; false when this desktop app can't (the caller falls back). */
	async open(url: string, title: string | null): Promise<boolean> {
		const desktop = getElectronAPI();
		if (!desktop?.teamsPrepareMeeting) return false;
		this.start();
		if (this.meeting?.url === url) {
			this.restore();
			return true;
		}
		const prepared = await desktop.teamsPrepareMeeting(url, title).catch((error) => {
			logger.warn('Teams meeting could not be prepared', error);
			return null;
		});
		if (!prepared) return false;
		runInAction(() => {
			this.clearLeftTimer();
			this.meeting = {url, title, partition: prepared.partition, src: prepared.src};
			this.state = LOADING;
			this.minimised = false;
			this.teamsControlsShown = false;
			this.joinedAt = null;
			this.captionsAsked = false;
		});
		desktop.teamsLayout?.({mini: false});
		return true;
	}

	control(action: TeamsControl): void {
		getElectronAPI()?.teamsControl?.(action);
	}

	toggleTeamsControls(): void {
		this.teamsControlsShown = !this.teamsControlsShown;
		this.control(this.teamsControlsShown ? 'show-teams-controls' : 'hide-teams-controls');
	}

	/** Hangs up in Teams, then closes the page once Teams has had time to leave properly. */
	leave(): void {
		const meeting = this.meeting;
		if (!meeting) return;
		this.control('leave');
		setTimeout(() => {
			if (this.meeting === meeting) this.close();
		}, CLOSE_AFTER_LEAVE_MS);
	}

	/** Removes Teams' page (pre-join, lobby, after the meeting); the desktop app then ends the meeting. */
	close(): void {
		this.clearLeftTimer();
		this.meeting = null;
		this.state = LOADING;
		this.minimised = false;
		this.teamsControlsShown = false;
		this.joinedAt = null;
	}

	minimise(): void {
		if (!this.meeting || this.minimised) return;
		this.minimised = true;
		getElectronAPI()?.teamsLayout?.({mini: true});
	}

	restore(): void {
		if (!this.minimised) return;
		this.minimised = false;
		getElectronAPI()?.teamsLayout?.({mini: false});
	}

	private onState(state: TeamsPageState): void {
		if (!this.meeting) return;
		const was = this.state.phase;
		this.state = state;
		if (state.phase === 'meeting') {
			this.clearLeftTimer();
			if (this.joinedAt === null) this.joinedAt = Date.now();
			this.maybeTurnOnCaptions(state);
		} else if (was === 'meeting' && !this.leftTimer) {
			// Teams' after-call page (the meeting ended, or the person left from Teams' own controls).
			const meeting = this.meeting;
			this.leftTimer = setTimeout(() => {
				this.leftTimer = null;
				if (this.meeting === meeting && !this.inMeeting) this.close();
			}, CLOSE_AFTER_LEFT_MS);
		}
		// A new Teams page forgets that its toolbar was shown.
		if (was !== state.phase && this.teamsControlsShown) this.control('show-teams-controls');
	}

	/** Konvo follows meetings through Teams' live captions: it turns them on once, after joining. */
	private maybeTurnOnCaptions(state: TeamsPageState): void {
		if (this.captionsAsked || state.captions || !TeamsFollow.following) return;
		this.captionsAsked = true;
		const meeting = this.meeting;
		setTimeout(() => {
			if (this.meeting === meeting && this.inMeeting && !this.state.captions) this.control('captions-on');
		}, CAPTIONS_AFTER_MS);
	}

	private clearLeftTimer(): void {
		if (this.leftTimer) clearTimeout(this.leftTimer);
		this.leftTimer = null;
	}
}

const store = new TeamsInKonvoStore();

// Dev builds (the sandbox) can drive the meeting frame from a script.
if (import.meta.env.DEV && typeof window !== 'undefined') {
	(window as unknown as {__konvoTeamsInKonvo?: TeamsInKonvoStore}).__konvoTeamsInKonvo = store;
}

export default store;
